import { router, usePage } from '@inertiajs/react'
import { Button, Callout, Dialog, Flex, RadioGroup, Text } from '@radix-ui/themes'
import { AlertCircle, Flag } from 'lucide-react'
import { useState } from 'react'
import { SharedProps } from '@/types'
import { EventsShowReportReason, EventsShowBrowsableCar as Driver } from '@/types/events_show'

type PageErrors = { errors?: { car_report?: string } }

/**
 * The "Report" link at the bottom of an expanded car card. Opening the dialog
 * is the browser's job (see ReportCarDialog): a counted report removes the car
 * from the list, which would take a dialog living inside the card with it.
 */
export function ReportCarLink({ car, onReport }: { car: Driver; onReport: (car: Driver) => void }) {
  const { props } = usePage<SharedProps>()

  if (car.mine) return null
  if (car.reported_by_me) {
    return (
      <Text size="1" color="gray">
        You reported this car. Thanks.
      </Text>
    )
  }
  if (!props.currentUser) {
    return (
      <a href={`/users/sign_in?return_to=${encodeURIComponent(window.location.pathname)}`} className="text-accent9 text-sm">
        Sign in to report
      </a>
    )
  }
  return (
    <Button size="1" variant="ghost" color="gray" onClick={() => onReport(car)}>
      <Flag size={12} /> Report
    </Button>
  )
}

interface Props {
  /** The car being reported; null keeps the dialog closed. */
  car: Driver | null
  reasons: EventsShowReportReason[]
  onClose: () => void
}

/**
 * "What's wrong?" — a small dialog rather than a page, because it's one
 * question. The reply is the same whether the report counted or the reporter
 * was over the limit.
 */
export default function ReportCarDialog({ car, reasons, onClose }: Props) {
  const { props } = usePage<SharedProps & PageErrors>()
  const [reason, setReason] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)

  const close = () => {
    setReason(null)
    setSent(false)
    onClose()
  }

  const submit = () => {
    if (!car || !reason) return
    setBusy(true)
    router.post(
      `/driver_cars/${car.id}/reports`,
      { reason },
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: (page) => {
          if (!(page.props as PageErrors).errors?.car_report) setSent(true)
        },
        onFinish: () => setBusy(false),
      },
    )
  }

  const error = !sent && props.errors?.car_report

  return (
    <Dialog.Root open={car !== null} onOpenChange={(open) => !open && close()}>
      <Dialog.Content maxWidth="420px">
        {sent ? (
          <>
            <Dialog.Title>Thanks</Dialog.Title>
            <Dialog.Description size="2">It's hidden while we take a look.</Dialog.Description>
            <Flex justify="end" mt="4">
              <Button onClick={close}>Close</Button>
            </Flex>
          </>
        ) : (
          car && (
            <>
              <Dialog.Title>What's wrong?</Dialog.Title>
              <Dialog.Description size="2" color="gray" mb="3">
                With {car.driver_name}'s {[car.year, car.make, car.model].filter(Boolean).join(' ')}
              </Dialog.Description>
              {error && (
                <Callout.Root color="red" mb="3">
                  <Callout.Icon>
                    <AlertCircle size={16} />
                  </Callout.Icon>
                  <Callout.Text>{error}</Callout.Text>
                </Callout.Root>
              )}
              <RadioGroup.Root value={reason ?? ''} onValueChange={setReason}>
                {reasons.map((r) => (
                  <RadioGroup.Item key={r.value} value={r.value}>
                    {r.label}
                  </RadioGroup.Item>
                ))}
              </RadioGroup.Root>
              <Flex gap="3" mt="4" justify="end">
                <Button variant="soft" color="gray" onClick={close}>
                  Cancel
                </Button>
                <Button color="red" disabled={!reason || busy} onClick={submit}>
                  Report
                </Button>
              </Flex>
            </>
          )
        )}
      </Dialog.Content>
    </Dialog.Root>
  )
}
